import { Grid } from "@material-ui/core";
import _ from "lodash";
import React from "react";
import { CSSTransition } from "react-transition-group";

import TextInput from "components/common/TextInput/TextInput";
import { LEVEL_PARTS } from "constants/index";
import { ILevel, TLevel } from "typings/meta-mirror";

import RichTextEditor from "../RichTextEditor";
import { Container, LevelHeading } from "./styles";
import { AreaSettingsProps, UpdateLevel } from "./types";

export const AreaSettings = ({
  areaIndex,
  areaName,
  flushRTEs,
  levels,
  mirrorLevelType,
  updateAreaLevels,
  updateAreaName,
  visible,
}: AreaSettingsProps) => {
  const [containerVisible, setContainerVisible] = React.useState(false);
  const [textInputValue, setTextInputValue] = React.useState(areaName);
  const [areaLevels, setAreaLevels] = React.useState(levels);
  const [levelNames, setLevelNames] = React.useState(
    Array.from(levels, (level) => level?.name ?? "")
  );

  const levelText = React.useMemo(
    () => (mirrorLevelType === "sequential" ? "Level" : "Part"),
    [mirrorLevelType]
  );

  const delayedUpdateAreaLevels = React.useCallback(
    _.debounce(
      (newLevels, index) =>
        updateAreaLevels(
          newLevels.map(({ name, ...rest }: any) => ({
            ...rest,
            name: name?.length ? name : undefined,
          })),
          index
        ),
      500
    ),
    []
  );

  const delayedUpdateAreaName = React.useCallback(
    _.debounce((newAreaName, index) => updateAreaName(newAreaName, index), 500),
    []
  );

  const getLevel = (level: ILevel) =>
    `${levelText} ${
      levelText === "Level" ? `${level.level}` : LEVEL_PARTS[level.level - 1]
    }`;

  const handleUpdateLevel: UpdateLevel = (index, key, value) => {
    const newLevels = { ...areaLevels };
    // const newLevels = _.cloneDeep(areaLevels);
    if (typeof newLevels[index] === "undefined") {
      newLevels[index] = {
        level: (index + 1) as TLevel,
        description: "",
        grade: null,
      };
    }
    //  @ts-ignore
    newLevels[index][key] = value;
    const converted: ILevel[] = Object.entries(newLevels).map(
      ([, value]): ILevel => value as ILevel
    );
    setAreaLevels(converted);
    delayedUpdateAreaLevels(converted, areaIndex);
  };

  React.useEffect(() => {
    areaName !== textInputValue && setTextInputValue(areaName);
    !_.isEqual(levels.sort(), areaLevels.sort()) && setAreaLevels(levels);
  }, [areaName, levels]);

  return (
    <CSSTransition
      in={visible}
      classNames="add-wheel-settings-page"
      timeout={1000}
      unmountOnExit
      mountOnEnter
      onExited={() => setContainerVisible(false)}
      onEnter={() => setContainerVisible(true)}
    >
      <Container visible={visible ?? containerVisible}>
        <Grid container direction="column">
          <TextInput
            value={textInputValue}
            label={`Area ${areaIndex + 1} Name`}
            onChange={({ target: { value } }) => {
              setTextInputValue(value);
              delayedUpdateAreaName(value, areaIndex);
            }}
            inputProps={{ maxLength: 40 }}
            fullWidth
          />
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="stretch"
            spacing={3}
            className="mt-2"
          >
            {levels.map((level, index) => (
              <Grid key={level.level + mirrorLevelType} item xs={12} lg={6}>
                <TextInput
                  value={levelNames[index]}
                  label={`${getLevel(level)} Name`}
                  name={`level-${level.level}-name`}
                  onChange={({ target: { value } }) => {
                    setLevelNames((prev) => {
                      const newLevelNames = [...prev];
                      newLevelNames[index] = value;
                      return newLevelNames;
                    });
                    handleUpdateLevel(index, "name", value);
                  }}
                  inputProps={{ maxLength: 40 }}
                  fullWidth
                />
                <LevelHeading variant="body1">
                  {getLevel(level)} Description
                </LevelHeading>

                <RichTextEditor
                  placeholder={`Insert ${getLevel(level)}
                   Description...`}
                  valueIn={level.description}
                  resetValue={flushRTEs}
                  setLevelDescription={(description: string) =>
                    handleUpdateLevel(index, "description", description)
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Container>
    </CSSTransition>
  );
};
